import { css, cx } from '@linaria/atomic';
import type { Row, TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { Checkbox } from '../../forms/atoms/checkbox.component';
import { useControlledField } from '../../forms/hooks/use-controlled-field/use-controlled-field';
import { Text } from '../../text/text.component';
import { TableData } from '../table-data-type';

const selectAllTextStyle = css`
  margin: 0;
  margin-left: ${theme.spacing(2)};
`;

const selectAllClickableStyle = css`
  cursor: pointer;
  text-decoration: underline;
  color: ${theme.colors.primary50};
`;

type SelectAllRowsProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  nonDisabledRows: Row<T>[];
};

export const SelectAllRows = <T extends TableData>({ tableInstance, nonDisabledRows }: SelectAllRowsProps<T>) => {
  const { selectedFlatRows, isMobile, flatRows, tableTrackingIds, rowSelectionConfig, state } = tableInstance;

  const selectedCount = Object.keys(state.selectedRowIds).length;
  const { selectAllLabel, onSelectAllLabelClick } = rowSelectionConfig || {};

  const allSelected = nonDisabledRows.every((row) => row.isSelected);
  const allUnselected = nonDisabledRows.every((row) => !row.isSelected);

  const selectAllProps = useControlledField({
    type: 'checkbox',
    value: !allUnselected,
    onChange: () => nonDisabledRows.forEach((row) => tableInstance.toggleRowSelected(row.id, !allSelected)),
  });

  const hasDisabledRows = nonDisabledRows.length !== flatRows.length;
  const checkboxFieldLabel = hasDisabledRows ? 'Select All Available' : 'Select All';

  const labelContent =
    typeof selectAllLabel === 'function'
      ? selectAllLabel({ selectedCount, tableInstance })
      : selectAllLabel || checkboxFieldLabel;

  const showLabel = !isMobile && (!selectedFlatRows.length || !!selectAllLabel);

  return (
    <>
      <Checkbox
        data-trackingid={tableTrackingIds.selectAll}
        disabled={tableInstance.isLoading || !flatRows.length || !nonDisabledRows.length}
        name='select-all-rows'
        indeterminate={!allSelected}
        {...selectAllProps}
        error={false}
      />
      {showLabel && (
        <Text
          className={cx(selectAllTextStyle, !!(onSelectAllLabelClick && selectedCount) && selectAllClickableStyle)}
          size='medium'
          color={tableInstance.isLoading || !flatRows.length || !nonDisabledRows.length ? 'disabled' : 'default'}
          onClick={onSelectAllLabelClick ? () => onSelectAllLabelClick({ selectedCount, tableInstance }) : undefined}
          {...(onSelectAllLabelClick && { role: 'button', tabIndex: 0 })}
        >
          {labelContent}
        </Text>
      )}
    </>
  );
};
